import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { saveAs } from 'file-saver';
import { Subject, Subscription, timer, EMPTY, Observable, concatMap } from 'rxjs';
import { takeUntil, tap, finalize, catchError } from 'rxjs/operators';

import { lumFileName } from '@app/shared/utils/file.utils';
import { DialogConfig } from 'v2/modules/shared/configs/dialog.config';
import { PaginationConfig } from 'v2/modules/shared/configs/paginator.config';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IPagination } from 'v2/modules/shared/interfaces/paginator.interface';
import { LumResponseError } from 'v2/shared/api/lum-response-error';

import { AutomationRuleConfig } from '../../config/automation-rule.config';
import { IAutomationRule } from '../../interfaces/automation-rule.interface';
import {
  LogEntry,
  TriggerEventRequest,
  TriggerEventResponse,
  LogContext,
  AutomationRuleApiService,
} from '../../services/automation-rule-api.service';

import { AutomationBuilderApiService } from './../../../shared/services/api/automation-builder-api.service';
import { AutomationRuleFormComponent } from './../automation-rule-form/automation-rule-form.component';

// INTERFACES

// SERVICES

// Define LogLevel type including 'All'
type LogLevel = LogEntry['level'] | 'All';

@Component({
  selector: 'lum-automation-rule',
  templateUrl: './automation-rule.component.html',
  styleUrls: ['./automation-rule.component.scss'],
})
export class AutomationRuleComponent implements OnInit, OnDestroy {
  relations :string = 'actions,conditions';
  rules :IAutomationRule[] = [];
  loading :boolean = false;
  automationRuleConfig = AutomationRuleConfig;

  ruleIds :{ id :number; name :string }[] = [];

  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    path: '',
    per_page: PaginationConfig.perPage,
    to: null,
    total: 0,
  };
  queryParams :{ [param :string] :string | string[] };

  testingForm :FormGroup;
  modelTypes :string[] = [
    'order',
    'invoice',
    'supplier_purchase_order',
    'order_item',
    'oms_product',
  ];
  eventNames :string[] = [
    'order_created',
    'order_updated',
    'invoice_created',
    'invoice_updated',
    'supplier_purchase_order_created',
    'supplier_purchase_order_updated',
    'supplier_purchase_order_approved',
    'supplier_purchase_order_rejected',
    'order_item_created',
    'sku_mapping_completed',
    'sku_mapping_failed',
  ];

  logs :LogEntry[] = [];
  filteredLogs :LogEntry[] = [];
  currentRunId :string | null = null;
  lastFetchTime :string | null = null;
  isPolling :boolean = false;
  selectedLogLevel :LogLevel = 'All';
  readonly logLevels :LogLevel[] = [
    'All',
    'ERROR',
    'WARNING',
    'INFO',
    'DEBUG',
    'SUCCESS',
  ];
  showJson :{ [key :number] :boolean } = {};

  @ViewChild('logContainer') private logContainer :ElementRef;

  readonly #destroy$ = new Subject<void>();
  #pollingSubscription :Subscription | null = null;

  constructor(
    private readonly dialog :MatDialog,
    private readonly automationBuilderApiService :AutomationBuilderApiService,
    private readonly automationApi :AutomationRuleApiService,
    private readonly snackbar :MatSnackBar,
    private readonly cd :ChangeDetectorRef,
    private readonly fb :FormBuilder,
  ) {}

  ngOnInit() :void {
    this.initQueryParams();
    this.loadRules();

    this.testingForm = this.fb.group({
      model_type: [null, Validators.required],
      model_id: [null, Validators.required],
      event_name: [null, Validators.required],
    });
  }

  ngOnDestroy() :void {
    this.#destroy$.next();
    this.#destroy$.complete();
    this.stopPolling();
  }

  private initQueryParams() :void {
    this.queryParams = {
      limit: PaginationConfig.perPage.toString(),
      page: PaginationConfig.page.toString(),
      sortDirection: 'desc',
      sortField: 'created_at',
      relations: this.relations,
    };
  }

  private loadRules() :void {
    this.loading = true;
    this.automationBuilderApiService
      .get({ ...this.queryParams })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.rules = response.data;
          this.pagination = response?.meta;
          this.cd.detectChanges();
        },
        error: (error :LumResponseError) => {
          this.loading = false;
          this.cd.detectChanges();
          this.snackbar.open(error.message, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onCreateRule() :void {
    this.dialog
      .open(AutomationRuleFormComponent, {
        width: DialogConfig.sizes['extra-large'],
        maxHeight: 'calc(100vh - 150px)',
      })
      .afterClosed()
      .pipe(takeUntil(this.#destroy$))
      .subscribe((rule) => {
        if (rule) {
          this.rules = [rule, ...this.rules];
          this.pagination.total = (this.pagination.total || 0) + 1;
          this.cd.detectChanges();
          this.snackbar.open('Rule created successfully', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
      });
  }

  onDeleteRule(id :number) :void {
    this.loading = true;
    this.automationBuilderApiService
      .delete(id)
      .pipe(
        takeUntil(this.#destroy$),
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          this.rules = this.rules.filter((rule) => rule.id !== id);
          this.pagination.total = (this.pagination.total || 0) - 1;
          this.snackbar.open('Rule deleted successfully', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error :LumResponseError) => {
          this.snackbar.open(error.message, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onUpdateRule(rule :IAutomationRule) :void {
    this.getRuleIds(rule.id);
    this.dialog
      .open(AutomationRuleFormComponent, {
        width: DialogConfig.sizes['extra-large'],
        maxHeight: 'calc(100vh - 150px)',
        data: {
          rule,
          ruleIds: this.ruleIds,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.#destroy$))
      .subscribe((updatedRule) => {
        if (updatedRule) {
          const index = this.rules.findIndex((r) => r.id === updatedRule.id);
          if (index > -1) {
            this.rules[index] = updatedRule;
            this.rules = [...this.rules];
            this.cd.detectChanges();
            this.snackbar.open('Rule updated successfully', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        }
      });
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.queryParams = {
      ...this.queryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    };
    this.loadRules();
  }

  getRuleIds(ruleId :number) :void {
    this.ruleIds = this.rules
      .filter((rule) => rule.id !== ruleId)
      .map((rule) => ({ id: rule.id, name: rule.name }));
  }

  triggerTest() :void {
    if (this.testingForm.invalid) {
      return;
    }

    this.stopPolling();
    this.logs = [];
    this.filteredLogs = [];
    this.currentRunId = null;
    this.lastFetchTime = null;
    this.showJson = {};
    this.selectedLogLevel = 'All';

    const requestData :TriggerEventRequest = this.testingForm.value;

    this.automationApi
      .triggerEvent(requestData)
      .pipe(
        takeUntil(this.#destroy$),
        tap(() => (this.isPolling = true)),
        catchError((error :LumResponseError) => {
          let message = error.message;
          const causeError = error.cause as any;
          if (
            causeError?.error?.errors &&
            typeof causeError.error.errors === 'object'
          ) {
            message = Object.values(causeError.error.errors).flat().join(', ');
          }
          this.snackbar.open(message || 'Failed to trigger event', 'Error', {
            duration: SnackBarConfig.ERROR.duration,
          });
          this.isPolling = false;
          this.cd.detectChanges();

          return EMPTY;
        }),
      )
      .subscribe((response :TriggerEventResponse) => {
        this.snackbar.open(
          `Test triggered successfully. Run ID: ${response.run_id}`,
          'Success',
          {
            duration: SnackBarConfig.DEFAULT.duration,
          },
        );
        console.log(
          '[AutomationRuleComponent] triggerTest success, calling startPolling with runId:',
          response.run_id,
        );
        this.startPolling(response.run_id);
        this.cd.detectChanges();
      });
  }

  private startPolling(runId :string) :void {
    console.log(
      '[AutomationRuleComponent] startPolling entered with runId:',
      runId,
    );
    this.currentRunId = runId;
    this.isPolling = true;
    this.cd.detectChanges();

    if (this.#pollingSubscription) {
      this.#pollingSubscription.unsubscribe();
    }

    this.#pollingSubscription = timer(0, 5000)
      .pipe(
        concatMap(() =>
          this.fetchLogs().pipe(
            catchError((error :LumResponseError) => {
              console.error('Error fetching logs:', error);
              this.snackbar.open(error.message || 'Failed to fetch logs', 'Error', {
                duration: SnackBarConfig.ERROR.duration,
              });

              return EMPTY;
            }),
          ),
        ),
        takeUntil(this.#destroy$),
      )
      .subscribe();
  }

  private fetchLogs() :Observable<LogEntry[]> {
    console.log(
      `[AutomationRuleComponent] fetchLogs called. Current runId: ${
        this.currentRunId
      }, Last fetch time: ${this.lastFetchTime}`,
    );
    if (!this.currentRunId) {
      console.warn(
        '[AutomationRuleComponent] fetchLogs called without currentRunId, skipping.',
      );

      return EMPTY;
    }

    console.log(
      `[AutomationRuleComponent] Calling automationApi.getLogs for runId: ${this.currentRunId}`,
    );

    return this.automationApi.getLogs(this.currentRunId, this.lastFetchTime).pipe(
      tap((newLogs :LogEntry[]) => {
        if (newLogs && newLogs.length > 0) {
          this.lastFetchTime = newLogs[newLogs.length - 1].timestamp;
          this.logs = [...this.logs, ...newLogs];
          this.applyFilters();
          this.cd.detectChanges();
          this.scrollToBottom();
        }
      }),
    );
  }

  public stopPolling() :void {
    if (this.#pollingSubscription) {
      this.#pollingSubscription.unsubscribe();
      this.#pollingSubscription = null;
    }
    this.isPolling = false;
    this.cd.detectChanges();
  }

  onLogLevelSelectionChange(selectedLevel :LogLevel | undefined) :void {
    if (selectedLevel) {
      this.selectedLogLevel = selectedLevel;
      this.applyFilters();
      this.cd.detectChanges();
    }
  }

  private applyFilters() :void {
    if (this.selectedLogLevel === 'All') {
      this.filteredLogs = [...this.logs];
    } else {
      this.filteredLogs = this.logs.filter(
        (log) => log.level === this.selectedLogLevel,
      );
    }
  }

  toggleJson(logId :number) :void {
    this.showJson[logId] = !this.showJson[logId];
    this.cd.detectChanges();
  }

  copyJson(context :LogContext | undefined) :void {
    if (!context) return;
    const jsonString = JSON.stringify(context, null, 2);
    navigator.clipboard
      .writeText(jsonString)
      .then(() => {
        this.snackbar.open('JSON copied to clipboard', 'Success', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      })
      .catch((err) => {
        console.error('Failed to copy JSON: ', err);
        this.snackbar.open('Failed to copy JSON', 'Error', {
          duration: SnackBarConfig.ERROR.duration,
        });
      });
  }

  downloadLog() :void {
    if (this.logs.length === 0) {
      return;
    }

    const logLines = this.logs.map((entry) => {
      const timestamp = new Date(entry.timestamp).toLocaleString();
      let line = `[${entry.level}] ${timestamp} - ${entry.description}`;
      if (entry.context && Object.keys(entry.context).length > 0) {
        line += `\nContext: ${JSON.stringify(entry.context, null, 2)}`;
      }

      return line;
    });

    const blob = new Blob([logLines.join('\n\n')], {
      type: 'text/plain;charset=utf-8',
    });
    const name = lumFileName(`automation_test_log_${this.currentRunId}`, '.log');

    saveAs(blob, name);

    this.snackbar.open('Log downloaded', 'Success', {
      duration: SnackBarConfig.DEFAULT.duration,
    });
  }

  private scrollToBottom() :void {
    setTimeout(() => {
      try {
        if (this.logContainer?.nativeElement) {
          this.logContainer.nativeElement.scrollTop =
            this.logContainer.nativeElement.scrollHeight;
        }
      } catch (err) {
        console.error('Could not scroll to bottom of log container:', err);
      }
    }, 0);
  }

  objectNotEmpty(obj :LogContext | undefined) :boolean {
    return !!obj && Object.keys(obj).length > 0;
  }

  trackLogById(index :number, item :LogEntry) :number {
    return item.id;
  }

  clearLogs() :void {
    this.stopPolling();
    this.logs = [];
    this.filteredLogs = [];
    this.showJson = {};
    this.currentRunId = null;
    this.lastFetchTime = null;
    this.snackbar.open('Activity log cleared', 'Success', {
      duration: SnackBarConfig.DEFAULT.duration,
    });
    this.cd.detectChanges();
  }

  // Public wrapper method to be called from the template
  public handleStopPollingClick() :void {
    this.stopPolling();
  }
}
